"use client";

import React, { useEffect, useState } from "react";
import { Row, Col, Card, Spinner, Form, Button, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import Swal from "sweetalert2";
import PaginationBarComp from "../Pagination/PaginationBar";

const DEFAULT_STORE_IMAGE = "/images/top-creator-1.png";

const getStoreImage = (store) => {
  return (
    store?.image ||
    store?.logo ||
    store?.logoUrl ||
    store?.imageUrl ||
    DEFAULT_STORE_IMAGE
  );
};

const UserStoresComp = () => {
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const fetchStores = async (searchTerm = searchValue) => {
    setLoading(true);

    try {
      const { data } = await api.get("/user-panel/stores", {
        params: {
          search: searchTerm || undefined,
        },
      });

      const items = Array.isArray(data)
        ? data
        : data?.items || data?.data || [];

      setStores(items);

      if (!Array.isArray(data)) {
        setTotalPages(data?.totalPages || 1);
      } else {
        setTotalPages(1);
      }
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "خطا",
        text: "خطا در دریافت لیست فروشگاه‌ها",
        confirmButtonText: "باشه",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const handleSearch = () => {
    setPage(1);
    setSearchValue(search);
    fetchStores(search);
  };

  const handleReset = () => {
    setSearch("");
    setSearchValue("");
    setPage(1);
    fetchStores("");
  };

  const handleStoreClick = (store) => {
    // code باید توسط API برگردانده شود.
    const storeCode =
      store?.code ||
      store?.storeCode;

    if (!storeCode) {
      Swal.fire({
        icon: "error",
        title: "خطا",
        text: "کد فروشگاه از API دریافت نشده است.",
        confirmButtonText: "باشه",
      });

      console.error("Store code is missing:", store);
      return;
    }

    navigate(`/user/stores/${encodeURIComponent(storeCode)}/products`, {
      state: {
        store,
      },
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h3 className="mb-1">فروشگاه‌ها</h3>
          <p className="text-secondary mb-0">
            فروشگاه مورد نظر خود را انتخاب کنید
          </p>
        </div>

        <Badge bg="primary" className="fs-6 px-3 py-2">
          {stores.length} فروشگاه
        </Badge>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm rounded-3 mb-4">
        <Card.Body>
          <div className="row g-2">
            <div className="col-md-8">
              <Form.Control
                size="lg"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                placeholder="جستجو در نام، تلفن، آدرس یا توضیحات..."
              />
            </div>

            <div className="col-md-2">
              <Button
                variant="primary"
                size="lg"
                className="w-100"
                onClick={handleSearch}
              >
                <i className="material-symbols-outlined align-middle me-1">
                  search
                </i>
                جستجو
              </Button>
            </div>

            <div className="col-md-2">
              <Button
                variant="light"
                size="lg"
                className="w-100 border"
                onClick={handleReset}
              >
                پاک کردن
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Stores */}
      {stores.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-3">
          <Card.Body className="text-center py-5">
            <i
              className="material-symbols-outlined text-secondary"
              style={{ fontSize: 64 }}
            >
              store
            </i>

            <h5 className="mt-3">فروشگاهی یافت نشد</h5>

            <p className="text-secondary mb-0">
              عبارت جستجو را تغییر دهید.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Row xs={1} sm={2} lg={3} xl={4} className="g-4">
          {stores.map((store) => (
            <Col key={store.code || store.id}>
              <Card
                className="h-100 border-0 rounded-4 shadow-sm"
                style={{
                  cursor: "pointer",
                  transition: "all .2s ease",
                  overflow: "hidden",
                }}
                onClick={() => handleStoreClick(store)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 30px rgba(0,0,0,.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 10px rgba(0,0,0,.06)";
                }}
              >
                <div
                  style={{
                    height: 180,
                    background: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={getStoreImage(store)}
                    alt={store.name || "فروشگاه"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_STORE_IMAGE;
                    }}
                  />
                </div>

                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <h5 className="fw-bold mb-2">
                      {store.name || "فروشگاه بدون نام"}
                    </h5>

                    <i className="material-symbols-outlined text-primary">
                      arrow_back
                    </i>
                  </div>

                  {store.address && (
                    <div className="text-secondary small mb-2">
                      <i className="material-symbols-outlined align-middle fs-18 me-1">
                        location_on
                      </i>
                      {store.address}
                    </div>
                  )}

                  {store.phone && (
                    <div className="text-secondary small mb-2">
                      <i className="material-symbols-outlined align-middle fs-18 me-1">
                        phone
                      </i>
                      {store.phone}
                    </div>
                  )}

                  {store.description && (
                    <p
                      className="text-secondary small mb-0"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {store.description}
                    </p>
                  )}
                </Card.Body>

                <Card.Footer className="bg-transparent border-0 px-4 pb-4 pt-0">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStoreClick(store);
                    }}
                  >
                    مشاهده محصولات
                    <i className="material-symbols-outlined align-middle ms-2">
                      arrow_back
                    </i>
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {totalPages > 1 && (
        <div className="mt-4">
          <PaginationBarComp
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            perPage={perPage}
            setPerPage={setPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default UserStoresComp;